/**
 * Ubezpieczenia — wspólne teksty modułu pod `ins.common.*` / `ins.enums.*` / `ins.errors.*`. Plik FUNDAMENTU — obszary
 * mogą DOPISYWAĆ brakujące etykiety enumów i kody błędów (bez zmiany istniejących), resztę trzymają w `ins-<obszar>.ts`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const insCommonPl = {
  nav: {
    dashboard: 'Ubezpieczenia',
    queue: 'Do kwalifikacji',
    claims: 'Szkody',
    tenders: 'Przetargi',
    policies: 'Polisy i umowy',
    analytics: 'Szkodowość',
    import: 'Import polis',
    settings: 'Ustawienia ubezpieczeń',
  },
  module: 'Ubezpieczenia i szkody',
  currency: 'zł',
  vehicleNone: 'bez pojazdu',
  demoBadge: 'Dane demo',
};

export const insCommonEn: Shape<typeof insCommonPl> = {
  nav: {
    dashboard: 'Insurance',
    queue: 'To qualify',
    claims: 'Claims',
    tenders: 'Tenders',
    policies: 'Policies & contracts',
    analytics: 'Loss ratio',
    import: 'Policy import',
    settings: 'Insurance settings',
  },
  module: 'Insurance & claims',
  currency: 'PLN',
  vehicleNone: 'no vehicle',
  demoBadge: 'Demo data',
};

export const insEnumsPl = {
  line: { OC: 'OC', AC: 'AC', NNW: 'NNW', ASS: 'Assistance', SZYBY: 'Szyby', CARGO: 'Cargo (ładunek)', OCP: 'OC przewoźnika' },
  path: {
    OC_OWN: 'Z naszego OC',
    AC_OWN: 'Z własnego AC',
    OC_THIRD_PARTY: 'Z OC sprawcy (regres)',
    CARGO: 'Szkoda w towarze',
    OWN_COST: 'Koszt własny',
  },
  stage: {
    QUALIFICATION: 'Kwalifikacja',
    REPORTED: 'Zgłoszona',
    ADJUSTMENT: 'Likwidacja',
    DECISION: 'Decyzja',
    SETTLEMENT: 'Rozliczenie',
    CLOSED: 'Zamknięta',
  },
  decision: { ACCEPTED: 'Uznana', PARTIAL: 'Uznana częściowo', REJECTED: 'Odmowa', APPEALED: 'Odwołanie' },
  sourceType: { FLEET_INCIDENT: 'Zgłoszenie floty', MANUAL: 'Wpis ręczny' },
  sourceDecision: { PENDING: 'Do decyzji', CLAIM: 'Szkoda założona', ATTACHED: 'Dołączone do szkody', NO_CLAIM: 'Bez szkody' },
  paymentDirection: { IN: 'Wpływ', OUT: 'Wydatek' },
  paymentKind: {
    INDEMNITY: 'Odszkodowanie',
    RECOURSE: 'Regres',
    OWN_SHARE: 'Udział własny',
    REPAIR: 'Naprawa',
    TOWING: 'Holowanie',
    DOWNTIME: 'Przestój',
    CARGO: 'Towar',
    OTHER: 'Inne',
  },
  tenderStatus: {
    PREPARATION: 'Przygotowanie',
    SENT: 'Zapytanie wysłane',
    QUESTIONS: 'Pytania oferentów',
    OFFERS_RECEIVED: 'Oferty złożone',
    EVALUATION: 'Ocena ofert',
    NEGOTIATION: 'Negocjacje',
    AWARDED: 'Wybrano ofertę',
    CONTRACT_SIGNED: 'Umowa podpisana',
    CANCELLED: 'Anulowane',
  },
  procedureType: { BROKER: 'Przez brokera', DIRECT: 'Zapytanie bezpośrednie', RENEWAL: 'Negocjacja odnowienia' },
};

export const insEnumsEn: Shape<typeof insEnumsPl> = {
  line: { OC: 'MTPL', AC: 'Own damage', NNW: 'PA', ASS: 'Assistance', SZYBY: 'Glass', CARGO: 'Cargo', OCP: 'Carrier liability' },
  path: {
    OC_OWN: 'Our liability (MTPL)',
    AC_OWN: 'Own damage (AC)',
    OC_THIRD_PARTY: 'Third-party liability (recourse)',
    CARGO: 'Cargo damage',
    OWN_COST: 'Own cost',
  },
  stage: {
    QUALIFICATION: 'Qualification',
    REPORTED: 'Reported',
    ADJUSTMENT: 'Adjustment',
    DECISION: 'Decision',
    SETTLEMENT: 'Settlement',
    CLOSED: 'Closed',
  },
  decision: { ACCEPTED: 'Accepted', PARTIAL: 'Partially accepted', REJECTED: 'Rejected', APPEALED: 'Appealed' },
  sourceType: { FLEET_INCIDENT: 'Fleet incident', MANUAL: 'Manual entry' },
  sourceDecision: { PENDING: 'Pending', CLAIM: 'Claim opened', ATTACHED: 'Attached to claim', NO_CLAIM: 'No claim' },
  paymentDirection: { IN: 'Inflow', OUT: 'Outflow' },
  paymentKind: {
    INDEMNITY: 'Indemnity',
    RECOURSE: 'Recourse',
    OWN_SHARE: 'Own share',
    REPAIR: 'Repair',
    TOWING: 'Towing',
    DOWNTIME: 'Downtime',
    CARGO: 'Cargo',
    OTHER: 'Other',
  },
  tenderStatus: {
    PREPARATION: 'Preparation',
    SENT: 'Request sent',
    QUESTIONS: 'Bidder questions',
    OFFERS_RECEIVED: 'Offers received',
    EVALUATION: 'Evaluation',
    NEGOTIATION: 'Negotiation',
    AWARDED: 'Awarded',
    CONTRACT_SIGNED: 'Contract signed',
    CANCELLED: 'Cancelled',
  },
  procedureType: { BROKER: 'Via broker', DIRECT: 'Direct request', RENEWAL: 'Renewal negotiation' },
};

/** Kody błędów API modułu (`code` w odpowiedzi) — obszary dopisują swoje. */
export const insErrorsPl = {
  ATTACHMENT_OWNER_NOT_FOUND: 'Nie znaleziono dokumentu ani rekordu, do którego należy.',
};

export const insErrorsEn: Shape<typeof insErrorsPl> = {
  ATTACHMENT_OWNER_NOT_FOUND: 'The record this document belongs to was not found.',
};
